export const csrfToken = () => {
  const meta = document.head.querySelector('meta[name=csrf-token]');

  return meta && meta.content;
};

export const csrfFetch = (url, opts = {}) => {
  const { headers = {}, method = 'GET', ...args } = opts;

  const pusher = window.App?.pusher || window.pusher;

  Object.assign(headers, {
    // so rails xhr? works
    'X-Requested-With': 'XMLHttpRequest',
    'X-CSRF-Token': csrfToken(),
    'X-PUSHER-SOCKET-ID': pusher?.connection.socket_id,
  });

  return fetch(url, {
    ...args,
    method: method.toUpperCase(),
    headers,
  }).then(response => {
    if (response.ok) {
      return response;
    }

    throw new Error(response.statusText);
  });
};

let nonce = null;

const loadCSPNonce = () => {
  const metaTag = document.querySelector('meta[name=csp-nonce]');
  return (nonce = metaTag && metaTag.content);
};
const cspNonce = () => nonce || loadCSPNonce();

export const appendScript = text => {
  if (!text) return;
  const script = document.createElement('script');

  script.innerHTML = text;
  script.setAttribute('nonce', cspNonce());
  script.type = 'text/javascript';
  script.dataset.appended = true;
  document.head.appendChild(script).parentNode.removeChild(script);
};

export const execJSFromResponse = async (url, options = {}) => {
  try {
    const res = await csrfFetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/javascript',
      },
      ...options,
    });
    appendScript(await res.text());
  } catch (resError) {
    throw new Error(resError.statusText);
  }
};

export function colorFromString(str, saturation, lightness) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    // eslint-disable-next-line no-bitwise
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const h = hash % 360;
  return `hsl(${h}, ${saturation}%, ${lightness}%)`;
}

export function timestampToDate(timestamp) {
  let t = `${timestamp}00000000`;
  t = t.replace('.', '').slice(0, 13);
  t = parseInt(t, 10);
  return new Date(t);
}

export function uuid() {
  let result = '';
  let i;
  let random;
  for (i = 0; i < 32; i++) {
    random = (Math.random() * 16) | 0; // eslint-disable-line

    if (i === 8 || i === 12 || i === 16 || i === 20) {
      result += '-';
    }
    result += (i === 12 ? 4 : i === 16 ? (random & 3) | 8 : random).toString(
      16
    ); // eslint-disable-line
  }
  return result;
}

function ymd(date) {
  return date.toISOString().slice(0, 10);
}

export function todayOrYesterday(dateToCompare, today = new Date()) {
  if (ymd(today) === ymd(dateToCompare)) {
    return 0;
  }

  let yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (ymd(yesterday) === ymd(dateToCompare)) {
    return 1;
  }

  return null;
}

export function compareVersions(version1, version2) {
  if (/[^0-9.]/.test(`${version1}${version2}`))
    throw new Error(
      'Unsupported version. Version must contain only numbers and dots.'
    );

  const v1 = String(version1).split('.').map(Number);
  const v2 = String(version2).split('.').map(Number);

  for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
    const num1 = v1[i] || 0;
    const num2 = v2[i] || 0;

    if (num1 < num2) {
      return -1;
    } else if (num1 > num2) {
      return 1;
    }
  }

  return 0;
}

export function uniq(arr) {
  return [...new Set(arr)];
}

/**
 * Usage
 * =====
 *
 * ```js
 *  class A {
    delegator = delegate(
      {
        getters: ['title'],
        methods: ['querySelector'],
        from: this,
        toProvider: () => document,
      }
    )
  }
  ```
 *
 */

export function extractImageSize(url) {
  const img = new Image();
  img.src = url;
  return new Promise((resolve, reject) => {
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => reject(new Error('Could not load image'));
  });
}

export function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function calculateMedian(numbers) {
  const sorted = numbers.sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  } else {
    return sorted[middle];
  }
}

// So we can use requestAnimationFrame with async/await
export function nextFrame() {
  return new Promise(requestAnimationFrame);
}

export function htmlToText(html) {
  const el = document.createElement('div');
  el.innerHTML = html;
  return el.innerText;
}

export const loadImageAsDataUrl = async (file, options = {}) => {
  options.canvas = true;

  const c = await import('blueimp-load-image');
  const loadImage = c.default;
  return await new Promise((resolve, reject) => {
    loadImage.parseMetaData(file, data => {
      if (data.exif) {
        options.orientation = data.exif.get('Orientation');
      }

      loadImage(
        file,
        canvas => {
          if (canvas.width === 0 || canvas.height === 0) {
            canvas.type = 'error';
          }

          if (canvas.type === 'error') {
            const error = new Error(`Could not load a file '${file.name}'`);
            error.file = file;
            reject(error);
          } else {
            resolve(canvas.toDataURL());
          }
        },
        options
      );
    });
  });
};

function gcd(a, b) {
  /* return greatest common divisor */
  return b == 0 ? a : gcd(b, a % b);
}

export function getAspectRatio(w, h) {
  const r = gcd(w, h);

  return [w / r, h / r];
}

export function compactObject(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_key, value]) => value !== null && value !== undefined && value !== ''
    )
  );
}

export function isElementInViewport(el, viewport) {
  const viewportRect = viewport.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const elTop = elRect.top - viewportRect.top;
  const elBottom = elRect.bottom - viewportRect.top;

  // Only completely visible elements return true:
  return elTop >= 0 && elBottom <= viewportRect.height;
}

export function getRelativeScrollTop(el, parent) {
  const contentRect = parent.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();

  return elRect.top - contentRect.top + parent.scrollTop;
}
